import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTrail } from "@react-spring/web";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import Select from "../../../../components/ui/Select";
import AppIcon from "../../../../components/AppIcon";
import Pagination from "../../../../components/Pagination";
import EntityDialog, { EntityFormData } from "./EntityDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";

const ENTITY_NAMES = ["Numow", "ITC", "ITG", "Murex", "Areeba", "IPT"];
const COUNTRIES = ["LB", "UAE", "UK", "CA", "USA"];
const EMPLOYEES_NUM = ["1-10", "10-50", "50-100", "100+"];
const PAY_CYCLES = ["Monthly", "Yearly", "Quarterly"];
const LAST_PAYROLLS = [
  "1 month ago", "2 months ago", "3 months ago", "4 months ago",
  "5 months ago", "6 months ago", "7 months ago",
];

const ENTITY_DATA = [
  { entityName: "Numow", country: "LB", NumOfEmployees: "10-50", payCycle: "Monthly", lastPayroll: "1 month ago" },
  { entityName: "ITG", country: "LB", NumOfEmployees: "100+", payCycle: "Monthly", lastPayroll: "1 months ago" },
  { entityName: "Murex", country: "LB", NumOfEmployees: "100+", payCycle: "Monthly", lastPayroll: "1 months ago" },
  { entityName: "ITC", country: "LB", NumOfEmployees: "100+", payCycle: "Monthly", lastPayroll: "1 months ago" },
  { entityName: "Areeba", country: "LB", NumOfEmployees: "100+", payCycle: "Monthly", lastPayroll: "1 months ago" },
  { entityName: "IPT", country: "LB", NumOfEmployees: "100+", payCycle: "Monthly", lastPayroll: "1 months ago" },
  { entityName: "AntWork", country: "LB", NumOfEmployees: "10-50", payCycle: "Monthly", lastPayroll: "4 months ago" },
  { entityName: "Exquitech", country: "UAE", NumOfEmployees: "100+", payCycle: "Monthly", lastPayroll: "1 months ago" },
];

const Trail: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => {
  const items = React.Children.toArray(children);
  const trail = useTrail(items.length, {
    config: { mass: 5, tension: 2000, friction: 200 },
    opacity: open ? 1 : 0,
    x: open ? 0 : 20,
    height: open ? 80 : 0,
    display: "flex",
    from: { opacity: 0, x: 20, height: 0 },
  });

  if (!open) return null;

  return (
    <>
      {trail.map(({ height }, index) => (
        <div key={index} className="overflow-hidden pe-0.5">
          <div style={{ height: height.get() }}>{items[index]}</div>
        </div>
      ))}
    </>
  );
};

const PAGE_SIZE = 10;

export default function Entities() {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Filter state
  const [filterEmployees, setFilterEmployees] = useState<string[]>([]);
  const [filterEntityName, setFilterEntityName] = useState<string[]>([]);
  const [filterCountry, setFilterCountry] = useState<string[]>([]);
  const [filterPayCycle, setFilterPayCycle] = useState<string[]>([]);
  const [filterLastPayroll, setFilterLastPayroll] = useState<string[]>([]);

  // Dialog state
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [editData, setEditData] = useState<EntityFormData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEntityName, setDeleteEntityName] = useState("");

  const filteredData = useMemo(() => {
    let result = ENTITY_DATA;
    if (filterEmployees.length) result = result.filter(r => filterEmployees.includes(r.NumOfEmployees));
    if (filterEntityName.length) result = result.filter(r => filterEntityName.includes(r.entityName));
    if (filterCountry.length) result = result.filter(r => filterCountry.includes(r.country));
    if (filterPayCycle.length) result = result.filter(r => filterPayCycle.includes(r.payCycle));
    if (filterLastPayroll.length) result = result.filter(r => filterLastPayroll.includes(r.lastPayroll));
    return result;
  }, [filterEmployees, filterEntityName, filterCountry, filterPayCycle, filterLastPayroll]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toOptions = (arr: string[]) => arr.map(v => ({ value: v, label: v }));

  const handleNewEntity = () => {
    setEditData(null);
    setEntityDialogOpen(true);
  };

  const handleEditEntity = (row: typeof ENTITY_DATA[0]) => {
    setEditData({
      entityName: row.entityName,
      country: row.country,
      numOfEmployees: row.NumOfEmployees,
      payCycle: row.payCycle,
    });
    setEntityDialogOpen(true);
  };

  const handleDeleteEntity = (name: string) => {
    setDeleteEntityName(name);
    setDeleteDialogOpen(true);
  };

  const handleEntitySubmit = () => {
    setEntityDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <div className="flex flex-col gap-3 pt-5 pe-8">
      <EntityDialog
        open={entityDialogOpen}
        onClose={() => setEntityDialogOpen(false)}
        onSubmit={handleEntitySubmit}
        editData={editData}
        options={{
          entityNames: ENTITY_NAMES,
          countries: COUNTRIES,
          employeesNum: EMPLOYEES_NUM,
          payCycles: PAY_CYCLES,
        }}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        entityName={deleteEntityName}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center items-center">
            <h1 className="text-2xl font-bold text-primary">{t("ENTITIES")}</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(f => !f)}
            className="w-[200px]"
          >
            {t("Filter")}
            <AppIcon name={showFilters ? "Minus" : "Plus"} size={16} className="ms-2" />
          </Button>
        </div>
        <Button onClick={handleNewEntity} className="w-[200px]">
          <AppIcon name="Users" size={16} className="me-2" />
          {t("New Entity")}
        </Button>
      </div>

      {/* Animated Filters */}
      <Trail open={showFilters}>
        <div className="flex flex-wrap gap-4 mt-6">
          <Select
            label={t("# Of Employees")}
            options={toOptions(EMPLOYEES_NUM)}
            value={filterEmployees}
            onChange={(val: any) => { setFilterEmployees(val || []); setCurrentPage(1); }}
            multiple
            className="w-[260px]"
          />
          <Select
            label={t("Entity Name")}
            options={toOptions(ENTITY_NAMES)}
            value={filterEntityName}
            onChange={(val: any) => { setFilterEntityName(val || []); setCurrentPage(1); }}
            multiple
            className="w-[260px]"
          />
          <Select
            label={t("Country")}
            options={toOptions(COUNTRIES)}
            value={filterCountry}
            onChange={(val: any) => { setFilterCountry(val || []); setCurrentPage(1); }}
            multiple
            className="w-[260px]"
          />
          <Select
            label={t("Pay Cycle")}
            options={toOptions(PAY_CYCLES)}
            value={filterPayCycle}
            onChange={(val: any) => { setFilterPayCycle(val || []); setCurrentPage(1); }}
            multiple
            className="w-[260px]"
          />
          <Select
            label={t("Last Payroll")}
            options={toOptions(LAST_PAYROLLS)}
            value={filterLastPayroll}
            onChange={(val: any) => { setFilterLastPayroll(val || []); setCurrentPage(1); }}
            multiple
            className="w-[260px]"
          />
        </div>
      </Trail>

      {/* Table */}
      <div className="w-full pt-5">
        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Entity Name")}</TableHead>
                <TableHead>{t("Country")}</TableHead>
                <TableHead>{t("# Of Employees")}</TableHead>
                <TableHead>{t("Pay Cycle")}</TableHead>
                <TableHead>{t("Last Payroll Plan")}</TableHead>
                <TableHead className="w-10" />
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.entityName} className="hover:bg-primary/10">
                  <TableCell className="font-semibold">{row.entityName}</TableCell>
                  <TableCell>{row.country}</TableCell>
                  <TableCell>{row.NumOfEmployees}</TableCell>
                  <TableCell>{row.payCycle}</TableCell>
                  <TableCell>{row.lastPayroll}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t("Delete")}
                      onClick={() => handleDeleteEntity(row.entityName)}
                    >
                      <AppIcon name="Trash2" size={16} />
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t("Edit")}
                      onClick={() => handleEditEntity(row)}
                    >
                      <AppIcon name="Pencil" size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
